import {
    alexVoiceRecording,
    alexaVoiceRecording,
    ameliaVoiceRecording,
    amrutVoiceRecording,
    bethVoiceRecording,
    callieVoiceRecording,
    carlyJVoiceRecording,
    cassidyVoiceRecording,
    chelseaVoiceRecording,
    elaraVoiceRecording,
    erynVoiceRecording,
    finnVoiceRecording,
    georgeVoiceRecording,
    halleyVoiceRecording,
    hazelVoiceRecording,
    hopeDefaultVoiceRecording,
    hopeV4VoiceRecording,
    jasonVoiceRecording,
    karenVoiceRecording,
    jerryBVoiceRecording,
    jessicaVoiceRecording,
    jessVoiceRecording,
    jhennyVoiceRecording,
    koreVoiceRecording,
    laurenV4VoiceRecording,
    laurenVoiceRecording,
    loriVoiceRecording,
    madeleineVoiceRecording,
    markVoiceRecording,
    mattVoiceRecording,
    mauricioVoiceRecording,
    mayaVoiceRecording,
    michaelVoiceRecording,
    peteVoiceRecording,
    puckVoiceRecording,
    sophieVoiceRecording,
    susanVoiceRecording,
    vedaSkyVoiceRecording,
    virajVoiceRecording,
} from "./voiceRecordingUrls";
import {
    callieSpanishVoice,
    carlyJVoice,
    chelseaVoice,
    elaraVoice,
    erynVoice,
    finnVoice,
    geminiKoreVoice,
    geminiPuckVoice,
    hopeEnglishVoice,
    hopeSpanishVoice,
    hopeV4Voice,
    jerryBVoice,
    laurenEnglishVoice,
    laurenSpanishVoice,
    laurenV4Voice,
    mayaVoice,
    sonioxHazelEnglishVoice,
    sonioxHazelSpanishVoice,
    vedaSkyVoice
} from "./voices";
import { voiceStringsMatch } from "../../utils/ttsVoiceString";

export type SparkVoiceLanguage = "english" | "spanish";
export type SparkVoiceTier = "standard" | "promptable" | "legacy";

export interface SparkVoiceOption {
    value: string
    label: string
    audioSrc?: string
    /** The TTS provider written to the telephony connection. */
    provider: string
    /** The English voice string retained for existing telephony consumers. */
    voice: string
    englishVoice: string
    spanishVoice: string
    tier: SparkVoiceTier
    /** Short note shown under the voice name in the picker. */
    detail?: string
    isDefault?: boolean
}

const callieEnglishVoice = "voice_id=7YaUDeaStRuoYg3FKsmU|model_id=eleven_flash_v2_5|speed=1.10|stability=1|similarity_boost=.4";
const sophieEnglishVoice = "voice_id=AEW6JTgnyoPaoB9zlK3S|model_id=eleven_flash_v2_5|speed=1.1|stability=0.5|similarity_boost=0.75";
const sophieSpanishVoice = "voice_id=AEW6JTgnyoPaoB9zlK3S|model_id=eleven_multilingual_v2|speed=0.97|stability=0.86|similarity_boost=0.75";
const loriEnglishVoice = "voice_id=TbMNBJ27fH2U0VgpSNko|model_id=eleven_flash_v2_5|speed=1.1|stability=1|similarity_boost=0.4";
const loriSpanishVoice = "voice_id=TbMNBJ27fH2U0VgpSNko|model_id=eleven_multilingual_v2|speed=1.1|stability=1|similarity_boost=0.4";

const elevenLabsV4Option = (value: string, voice: string, audioSrc: string, isDefault = false): SparkVoiceOption => ({
    value,
    label: value,
    audioSrc,
    provider: "ElevenLabs",
    voice,
    englishVoice: voice,
    spanishVoice: voice,
    tier: "standard",
    detail: "ElevenLabs",
    isDefault
});

const sonioxHazelOption: SparkVoiceOption = {
    value: "Hazel",
    label: "Hazel",
    audioSrc: hazelVoiceRecording,
    provider: "Soniox",
    voice: sonioxHazelEnglishVoice,
    englishVoice: sonioxHazelEnglishVoice,
    spanishVoice: sonioxHazelSpanishVoice,
    tier: "standard",
    detail: "Soniox"
};

const geminiOption = (value: string, voice: string, audioSrc: string): SparkVoiceOption => ({
    value,
    label: value,
    audioSrc,
    provider: "Gemini",
    voice,
    englishVoice: voice,
    spanishVoice: voice,
    tier: "promptable",
    detail: "Gemini 3.8 Flash"
});

/** Voices offered in every telephony voice picker. The first one is the default. */
export const voiceOptions: SparkVoiceOption[] = [
    elevenLabsV4Option("Hope", hopeV4Voice, hopeV4VoiceRecording, true),
    elevenLabsV4Option("Elara", elaraVoice, elaraVoiceRecording),
    elevenLabsV4Option("Veda Sky", vedaSkyVoice, vedaSkyVoiceRecording),
    elevenLabsV4Option("Lauren", laurenV4Voice, laurenV4VoiceRecording),
    elevenLabsV4Option("Maya", mayaVoice, mayaVoiceRecording),
    elevenLabsV4Option("Jerry B", jerryBVoice, jerryBVoiceRecording),
    elevenLabsV4Option("Finn", finnVoice, finnVoiceRecording),
    elevenLabsV4Option("Eryn", erynVoice, erynVoiceRecording),
    elevenLabsV4Option("Chelsea", chelseaVoice, chelseaVoiceRecording),
    elevenLabsV4Option("Carly J", carlyJVoice, carlyJVoiceRecording),
    sonioxHazelOption,
    geminiOption("Puck", geminiPuckVoice, puckVoiceRecording),
    geminiOption("Kore", geminiKoreVoice, koreVoiceRecording)
];

export const defaultVoiceOption: SparkVoiceOption = voiceOptions.find(option => option.isDefault) || voiceOptions[0];

const legacyOption = (
    value: string,
    label: string,
    englishVoice: string,
    spanishVoice: string = englishVoice,
    audioSrc?: string,
    provider: string = "elevenlabs"
): SparkVoiceOption => ({
    value,
    label,
    audioSrc,
    provider,
    voice: englishVoice,
    englishVoice,
    spanishVoice,
    tier: "legacy"
});

const legacyTurboVoice = (voiceId: string) => `voice_id=${voiceId}|model_id=eleven_turbo_v2|stability=0.8|similarity_boost=0.8`;

/**
 * Voices that are no longer offered but are still recognized, so existing
 * connections keep showing a friendly name instead of a raw voice string.
 */
export const legacyVoiceOptions: SparkVoiceOption[] = [
    legacyOption("Hope Classic", "Hope (classic)", hopeEnglishVoice, hopeSpanishVoice, hopeDefaultVoiceRecording),
    legacyOption("Callie", "Callie", callieEnglishVoice, callieSpanishVoice, callieVoiceRecording),
    legacyOption("Sophie", "Sophie", sophieEnglishVoice, sophieSpanishVoice, sophieVoiceRecording),
    legacyOption("Lauren Classic", "Lauren (classic)", laurenEnglishVoice, laurenSpanishVoice, laurenVoiceRecording),
    legacyOption("Lori", "Lori", loriEnglishVoice, loriSpanishVoice, loriVoiceRecording),
    legacyOption("Alexa", "Alexa", legacyTurboVoice("t51fJYm2fmmjCQAzkR5M"), undefined, alexaVoiceRecording),
    legacyOption("Madeleine", "Madeleine", legacyTurboVoice("kLVCa59XE4oxfhTnAvdc"), undefined, madeleineVoiceRecording),
    legacyOption("Susan", "Susan", legacyTurboVoice("P7x743VjyZEOihNNygQ9"), undefined, susanVoiceRecording),
    legacyOption("Sophie Classic", "Sophie (classic)", legacyTurboVoice("sKt7Hq8ey0SpJckxX1by")),
    legacyOption("George", "George", legacyTurboVoice("Cqvi37TsYKey25WXyNgD"), undefined, georgeVoiceRecording),
    legacyOption("Jason", "Jason", legacyTurboVoice("tzRU80bFwNhvoFZKHqEK"), undefined, jasonVoiceRecording),
    legacyOption("Alex", "Alex", legacyTurboVoice("zf6z4U9028YOsb3qu50O"), undefined, alexVoiceRecording),
    legacyOption("Mauricio", "Mauricio", legacyTurboVoice("94zOad0g7T7K4oa7zhDq"), undefined, mauricioVoiceRecording),
    legacyOption("Mark", "Mark", legacyTurboVoice("UgBBYS2sOqTuMpoF3BR0"), undefined, markVoiceRecording),
    legacyOption("Amelia", "Amelia", legacyTurboVoice("ZF6FPAbjXT4488VcRRnw"), undefined, ameliaVoiceRecording),
    legacyOption("Amrut", "Amrut", legacyTurboVoice("vO7hjeAjmsdlGgUdvPpe"), undefined, amrutVoiceRecording),
    legacyOption("Michael", "Michael", legacyTurboVoice("ashjVK50jp28G73AUTnb"), undefined, michaelVoiceRecording),
    legacyOption("Viraj", "Viraj", legacyTurboVoice("1U02n4nD6AdIZ9CjF053"), undefined, virajVoiceRecording),
    legacyOption("Halley", "Halley", legacyTurboVoice("eBvoGh8YGJn1xokno71w"), undefined, halleyVoiceRecording),
    legacyOption("Cassidy", "Cassidy", legacyTurboVoice("56AoDkrOh6qfVPDXZ7Pt"), undefined, cassidyVoiceRecording),
    legacyOption("Jhenny", "Jhenny", legacyTurboVoice("FXGrCtY3PEyfqczBAlqm"), undefined, jhennyVoiceRecording),
    legacyOption("Beth", "Beth", legacyTurboVoice("8N2ng9i2uiUWqstgmWlH"), undefined, bethVoiceRecording),
    legacyOption("Jessica", "Jessica", legacyTurboVoice("FZlijOvmHCYM8RpAxXLJ"), undefined, jessicaVoiceRecording),
    legacyOption("Pete", "Pete", legacyTurboVoice("ChO6kqkVouUn0s7HMunx"), undefined, peteVoiceRecording),
    legacyOption("Jess", "Jess", legacyTurboVoice("ys3XeJJA4ArWMhRpcX1D"), undefined, jessVoiceRecording),
    legacyOption("Matt", "Matt", legacyTurboVoice("pwMBn0SsmN1220Aorv15"), undefined, mattVoiceRecording),
    legacyOption("Karen", "Karen", legacyTurboVoice("LHgFk7RaIiyNE5I3pC1d"), undefined, karenVoiceRecording),
    legacyOption("Lauren Multilingual", "Lauren (multilingual)", "voice_id=3liN8q8YoeB9Hk6AboKe|model_id=eleven_multilingual_v2|stability=0.8|similarity_boost=0.8|speed=.97")
];

/** Offered and legacy voices, for recognizing voice strings already saved on a connection. */
export const allKnownVoiceOptions: SparkVoiceOption[] = [...voiceOptions, ...legacyVoiceOptions];

export const getSparkVoiceOption = (value: string): SparkVoiceOption | undefined => (
    allKnownVoiceOptions.find(option => option.value === value)
);

const getSparkVoiceLanguageForLocale = (locale: string): SparkVoiceLanguage => {
    const normalizedLocale = locale.toLowerCase();
    const localeLanguage = normalizedLocale.split(/[-_]/)[0];
    return normalizedLocale === "spanish" || localeLanguage === "es"
        ? "spanish"
        : "english";
};

/** Finds the voice a saved voice string belongs to, ignoring any Gemini style prompt. */
export const findSparkVoiceOptionByVoice = (
    voice: string,
    locale?: string
): SparkVoiceOption | undefined => {
    if (!locale) {
        return allKnownVoiceOptions.find(option => (
            voiceStringsMatch(option.englishVoice, voice) || voiceStringsMatch(option.spanishVoice, voice)
        ));
    }

    const language = getSparkVoiceLanguageForLocale(locale);
    return allKnownVoiceOptions.find(option => (
        language === "spanish"
            ? voiceStringsMatch(option.spanishVoice, voice)
            : voiceStringsMatch(option.englishVoice, voice)
    ));
};

export const getSparkVoiceString = (
    voice: string | SparkVoiceOption,
    language: SparkVoiceLanguage
): string | undefined => {
    const option = typeof voice === "string" ? getSparkVoiceOption(voice) : voice;
    if (!option) return undefined;

    return language === "spanish" ? option.spanishVoice : option.englishVoice;
};
